/** API status → English label (same tone as Sign in / Add To Cart). */
export const DASHBOARD_STATUS_LABELS = {
	ordered: 'Already ordered',
	not_ordered: 'No order yet',
	cancelled: 'Cancelled',
	inactive: 'Inactive',
};

export function dashboardStatusLabel(status) {
	return DASHBOARD_STATUS_LABELS[status] || status || 'No order yet';
}

export function canOrderForChild(child) {
	if (!child) return false;
	if (child.is_active === false) return false;
	if (child.family_is_active === false) return false;
	if (child.status === 'inactive') return false;
	return true;
}

/**
 * BE may send a JSON array, or a stringified list (sometimes Python-style quotes).
 * Non-arrays that cannot be parsed become [].
 */
export function normalizeServingTimes(raw) {
	if (Array.isArray(raw)) return raw;
	if (typeof raw === 'string' && raw.trim()) {
		try {
			const parsed = JSON.parse(raw.replace(/'/g, '"'));
			if (Array.isArray(parsed)) return parsed;
		} catch {
			/* ignore */
		}
	}
	return [];
}

/**
 * Lines to show on a child card.
 * - Meal sessions (Lunch/Snack/…): only when quantity > 0, or legacy `is_void`.
 * - Special BE bucket `Void`: voided order-product count — only when quantity > 0.
 */
export function visibleServingTimeLines(raw) {
	return normalizeServingTimes(raw).filter((st) => {
		if (!st || st.name == null || st.name === '') return false;
		const qty = Number(st.quantity) || 0;
		const isVoidBucket = String(st.name).toLowerCase() === 'void';
		if (isVoidBucket) return qty > 0;
		if (st.is_void) return true;
		return qty > 0;
	});
}

/** English label for one serving_times row (Void bucket → "Voided N"). */
export function servingTimeDisplayLabel(st) {
	const name = String(st?.name ?? '');
	const qty = st?.quantity ?? 0;
	if (name.toLowerCase() === 'void') {
		return `Voided ${qty}`;
	}
	let label = `${name} ${qty}`;
	if (st?.is_void) label += ' (void)';
	return label;
}

/** Slots shown on the meal-planner Home card (preview). */
export const MEAL_PLANNER_SLOTS = ['Lunch', 'Snack'];

export function isChildVisibleOnPlanner(child) {
	if (!child) return false;
	if (child.is_active === false) return false;
	if (child.status === 'inactive') return false;
	return true;
}

/**
 * Paid dish lines from dashboard `serving_times[].items` (P2-BE-01 / PA2-09).
 * Skips Void bucket and zero-qty rows. Safe when `items` is missing (Phase 1 shape).
 */
export function paidDishLinesFromServingTimes(raw) {
	const lines = [];
	normalizeServingTimes(raw).forEach((st) => {
		if (!st?.name || String(st.name).toLowerCase() === 'void') return;
		const items = Array.isArray(st.items) ? st.items : [];
		items.forEach((item, index) => {
			if (!item) return;
			const quantity = Number(item.quantity) || 0;
			if (quantity <= 0) return;
			const unitPrice = Number(item.unit_price) || 0;
			const lineTotal =
				item.line_total != null && item.line_total !== ''
					? Number(item.line_total) || 0
					: unitPrice * quantity;
			lines.push({
				key:
					item.order_product_id != null
						? String(item.order_product_id)
						: `${st.name}-${item.product_id ?? 'p'}-${index}`,
				servingTimeName: String(st.name),
				name: item.name || 'Item',
				quantity,
				unitPrice,
				lineTotal,
			});
		});
	});
	return lines;
}

/**
 * Slot summary for Home cards.
 * paidQty from dashboard serving_times; cartQty from unpaid Redux lines for that date.
 */
export function plannerSlotSummaries(servingTimes, cartLinesForDate) {
	const paidByName = {};
	normalizeServingTimes(servingTimes).forEach((st) => {
		if (!st?.name || String(st.name).toLowerCase() === 'void') return;
		paidByName[String(st.name)] = (paidByName[String(st.name)] || 0) + (Number(st.quantity) || 0);
	});

	const cartByName = {};
	(cartLinesForDate || []).forEach((line) => {
		const name = line.serving_time?.name || 'Lunch';
		cartByName[name] = (cartByName[name] || 0) + (Number(line.quantity) || 0);
	});

	return MEAL_PLANNER_SLOTS.map((slot) => {
		const paidQty = paidByName[slot] || 0;
		const cartQty = cartByName[slot] || 0;
		let detail = 'No selection';
		if (paidQty > 0 && cartQty > 0) detail = `${paidQty} paid · ${cartQty} in cart`;
		else if (paidQty > 0) detail = `${paidQty} paid`;
		else if (cartQty > 0) detail = `${cartQty} in cart`;
		return { name: slot, paidQty, cartQty, detail };
	});
}

export function plannerCardBadge({ paidQtyTotal, cartQtyTotal, child }) {
	if (!canOrderForChild(child) || child?.status === 'inactive') {
		return { label: 'Inactive', tone: 'muted' };
	}
	if (cartQtyTotal > 0) {
		return { label: 'Saved in cart · Unpaid', tone: 'unpaid' };
	}
	if (paidQtyTotal > 0 || child?.status === 'ordered') {
		return { label: 'Confirmed · Paid', tone: 'paid' };
	}
	if (child?.status === 'cancelled') {
		return { label: 'Cancelled', tone: 'muted' };
	}
	return { label: 'No selection', tone: 'muted' };
}
